import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usePagination } from "@/hooks/usePagination";
import { throwIfNotOk } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { formatDateWithOptionsBs, formatTimeWithOptionsBs } from "@/lib/format";
import { Pagination } from "@/components/ui/pagination";
import {
  Calendar,
  CircleNotch,
  Clock,
  User,
  ArrowsDownUp,
} from "@phosphor-icons/react";
import { getSessionStatusConfig } from "@/lib/status-configs";

export const Route = createFileRoute("/dashboard/supervisor/sessions")({
  component: SessionsPage,
});

interface SupervisorSession {
  id?: string;
  scheduledAt: string;
  clientName: string;
  status: "scheduled" | "completed" | "cancelled" | "no_show";
  durationMinutes: number;
}

interface PaginatedSupervisorSessions {
  sessions: SupervisorSession[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

function SessionsPage() {
  const { t } = useTranslation();
  const STATUS_CONFIG = getSessionStatusConfig(t);

  const TABS = [
    { value: "", label: t("supervisor.sessions.allSessions") },
    { value: "scheduled", label: t("status.scheduledPlural") },
    { value: "completed", label: t("status.completedPlural") },
    { value: "cancelled", label: t("status.cancelledPlural") },
  ];

  const { page, setPage, filters, setFilter, pageSize } = usePagination(
    { pageSize: 12 },
    { status: "" }
  );

  const { data, isLoading } = useQuery({
    queryKey: ["supervisor", "sessions", page, pageSize, filters.status],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      if (filters.status) params.set("status", filters.status as string);
      const res = await fetch(`/api/supervisor/sessions?${params}`);
      await throwIfNotOk(res, t("supervisor.sessions.errorLoading"));
      return res.json() as Promise<PaginatedSupervisorSessions>;
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="overflow-hidden rounded-2xl border border-border/60 shadow-sm" style={{ background: "linear-gradient(135deg, oklch(28% 0.04 84), oklch(22% 0.03 80))" }}>
        <div className="px-6 py-8 sm:px-8">
          <h1 className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }}>
            {t("supervisor.sessions.title")}
          </h1>
          <p className="mt-1 text-sm" style={{ color: "oklch(75% 0.02 84)" }}>
            {isLoading ? t("common.loading") : `${data?.total ?? 0} ${t("supervisor.sessions.totalSessions")}`}
          </p>

          {/* Tabs */}
          <div className="mt-5 flex gap-1.5 overflow-x-auto">
            {TABS.map((tab) => {
              const active = filters.status === tab.value;
              return (
                <button
                  key={tab.value}
                  onClick={() => setFilter("status", tab.value)}
                  className={cn(
                    "shrink-0 rounded-lg px-3.5 py-1.5 text-xs font-medium transition-all",
                    active
                      ? "bg-white text-foreground shadow-sm"
                      : "text-white/60 hover:bg-white/10 hover:text-white"
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl border border-border/40 bg-card" />
          ))}
        </div>
      ) : !data?.sessions.length ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 py-16">
          <div className="flex size-14 items-center justify-center rounded-full" style={{ background: "oklch(92% 0.04 84)" }}>
            <Calendar className="size-6" style={{ color: "oklch(55% 0.1 84)" }} />
          </div>
          <p className="mt-4 text-sm font-medium text-foreground">{t("supervisor.sessions.noSessions")}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {filters.status ? t("supervisor.sessions.tryDifferentFilter") : t("supervisor.sessions.sessionsWillAppear")}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm">
            <table className="w-full">
              <thead>
                <tr style={{ background: "oklch(97.5% 0.005 84)" }}>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    <span className="inline-flex items-center gap-1">{t("supervisor.sessions.date")} <ArrowsDownUp className="size-3 opacity-40" /></span>
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{t("supervisor.sessions.employee")}</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{t("supervisor.sessions.status")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {data.sessions.map((s, index) => {
                  const cfg = STATUS_CONFIG[s.status] ?? STATUS_CONFIG.scheduled;
                  const StatusIcon = cfg.icon;
                  return (
                    <tr key={s.id ?? index} className="transition-colors hover:bg-muted/20">
                      <td className="whitespace-nowrap px-5 py-3.5">
                        <div className="text-sm font-medium text-foreground">
                          {formatDateWithOptionsBs(s.scheduledAt, { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/Sarajevo" })}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTimeWithOptionsBs(s.scheduledAt, { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Sarajevo" })}
                          {" · "}{s.durationMinutes} min
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div
                            className="flex size-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
                            style={{ background: "oklch(92% 0.04 84)", color: "oklch(50% 0.1 84)", fontFamily: "var(--font-heading)" }}
                          >
                            {s.clientName.charAt(0)}
                          </div>
                          <span className="text-sm text-foreground">{s.clientName}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span
                          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                          style={{ background: cfg.bg, color: cfg.color }}
                        >
                          <StatusIcon className="size-3" />
                          {cfg.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2.5">
            {data.sessions.map((s, index) => {
              const cfg = STATUS_CONFIG[s.status] ?? STATUS_CONFIG.scheduled;
              const StatusIcon = cfg.icon;
              return (
                <div key={s.id ?? index} className="overflow-hidden rounded-xl border border-border/50 bg-card">
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                          style={{ background: "oklch(92% 0.04 84)", color: "oklch(50% 0.1 84)", fontFamily: "var(--font-heading)" }}
                        >
                          {s.clientName.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{s.clientName}</p>
                        </div>
                      </div>
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{ background: cfg.bg, color: cfg.color }}
                      >
                        <StatusIcon className="size-2.5" />
                        {cfg.label}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center gap-4 border-t border-border/30 pt-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="size-3" />
                        {formatDateWithOptionsBs(s.scheduledAt, { day: "numeric", month: "short", timeZone: "Europe/Sarajevo" })}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="size-3" />
                        {formatTimeWithOptionsBs(s.scheduledAt, { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Sarajevo" })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {data.totalPages > 1 && (
            <Pagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} />
          )}
        </>
      )}
    </div>
  );
}
